/**
 * MapManager - Handles map initialization, styles, camera, and controls
 * Manages Mapbox GL JS instance and configuration
 */
class MapManager {
    static CONFIG = {
        CONTAINER_ID: 'map',
        STYLE_URL: 'mapbox://styles/faasintra/cmk4jc0ct005u01ss0t15d6fy?optimize=true',
        SATELLITE_STYLE_URL: 'mapbox://styles/faasintra/cmk4jeu9u008n01svbj603g97',
        ATTRIBUTION: '© FAAS Architecture',
        // ! Desktop defaults
        DEFAULT_CENTER: { lng: -97.18372, lat: 58.80331 },
        DEFAULT_ZOOM: 3.5,
        DEFAULT_BEARING: 0,
        DEFAULT_PITCH: 30.50,
        // ! Mobile defaults
        MOBILE_ZOOM: 1.5,
        MOBILE_PITCH: 40,
        // ! Intro animation
        INTRO_ZOOM_DESKTOP: 3.8,
        INTRO_ZOOM_MOBILE: 2,
        INTRO_DURATION: 7500,
        INTRO_SPEED: 0.25,
        INTRO_CURVE: 1,
        ANTIALIAS: true,
        OPTIMIZE: true
    };
    /**
     * ! Create MapManager instance
     * @param {Object} options - Optional configuration overrides
     */
    constructor(options = {}) {
        // ! Map instance
        this.map = null;
        // ! Reactive mobile detection
        this.isMobile = false;
        this._setupMobileMediaQuery();
        // ! Configuration (merge with defaults, adjust for mobile)
        const baseConfig = { ...MapManager.CONFIG };
        if (this.isMobile) {
            baseConfig.DEFAULT_ZOOM = MapManager.CONFIG.MOBILE_ZOOM;
            baseConfig.DEFAULT_PITCH = MapManager.CONFIG.MOBILE_PITCH;
        }
        this.config = {
            ...baseConfig,
            ...options
        };
        // ! State
        this.isInitialized = false;
        this.isStyleLoaded = false;
        this.isSatelliteView = false;
        this.dataLayers = []; // * Track data layers to preserve during style switch
        // Event callbacks
        this.onLoadCallbacks = [];
        this.onErrorCallbacks = [];
    }
    /**
     * Setup reactive mobile detection with MediaQuery listener
     * Uses 767px breakpoint for UI responsiveness
     * @private
     */
    _setupMobileMediaQuery() {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        this.isMobile = mediaQuery.matches;
        // Listen for breakpoint changes
        mediaQuery.addEventListener('change', (e) => {
            this.isMobile = e.matches;
        });
    }
    /**
     * ! Initialize Mapbox map with token
     * @param {string} accessToken - Mapbox GL access token -- from tokens.js > DataManager
     * @returns {Promise<mapboxgl.Map>} Initialized map instance
     */
    async initialize(accessToken) {
        if (this.isInitialized) {
            return this.map;
        }
        try {
            // ! Set access token
            mapboxgl.accessToken = accessToken;
            // ! Create map instance
            this.map = new mapboxgl.Map({
                style: this.config.STYLE_URL,
                container: this.config.CONTAINER_ID,
                center: this.config.DEFAULT_CENTER,
                zoom: this.config.DEFAULT_ZOOM,
                bearing: this.config.DEFAULT_BEARING,
                pitch: this.config.DEFAULT_PITCH,
                antialias: this.config.ANTIALIAS,
                customAttribution: this.config.ATTRIBUTION
            });
            this._setupEventListeners(); // ! Set up event listeners
            this._addMapControls(); // ! Add map controls
            await this._waitForStyleLoad(); // ! Wait for style to load
            this._playIntroAnimation(); // ! Start intro animation
            this.isInitialized = true;
            return this.map;
        } catch (error) {
            this._triggerErrorCallbacks(error);
            throw error;
        }
    }
    /**
     * Wait for map style to fully load
     * @returns {Promise<void>}
     * @private
     */
    _waitForStyleLoad() {
        return new Promise((resolve, reject) => {
            if (this.map.isStyleLoaded()) {
                this.isStyleLoaded = true;
                resolve();
                return;
            }
            this.map.on('load', () => {
                this.isStyleLoaded = true;
                this._triggerLoadCallbacks();
                resolve();
            });
            this.map.on('error', (error) => {
                this._triggerErrorCallbacks(error);
                reject(error);
            });
            // * Timeout after 30 seconds
            setTimeout(() => {
                if (!this.isStyleLoaded) {
                    const error = new Error('Map load timeout after 30 seconds');
                    reject(error);
                }
            }, 30000);
        });
    }
    /**
     * Set up map event listeners
     * @private
     */
    _setupEventListeners() { // Map interaction events (no logging)
        this.map.on('movestart', () => {});
        this.map.on('moveend', () => {});
    }
    /**
     * Add map controls (geolocation with custom button)
     * @private
     */
    _addMapControls() {
        // Create geolocate control but don't add it to the map (we'll use custom button)
        this.geolocateControl = new mapboxgl.GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true // Use GPS if available
            },
            trackUserLocation: true, // Keep tracking as user moves
            showUserHeading: true, // Show direction user is facing
            showUserLocation: true // Show blue dot for user location
        });
        this.map.addControl(this.geolocateControl, 'top-left'); // Add Northing control, REQUIRED for functionality - ".is--hidden" is applied via CSS)
        this._setupCustomGeolocateButton(); // Connect custom button to geolocate control
        this._setupSatelliteToggle(); // Setup satellite toggle button
    }
    /**
     * Setup custom geolocate button to trigger native Mapbox geolocation
     * @private
     */
    _setupCustomGeolocateButton() {
        const customButton = document.getElementById('geolocate');
        if (!customButton) return;
        customButton.addEventListener('click', () => { // Trigger geolocate when custom button is clicked
            if (this.geolocateControl) {
                this.geolocateControl.trigger();
            }
        });
        // ! Listen to geolocate events to update button state
        this.geolocateControl.on('geolocate', () => {
            customButton.classList.add('is--active');
        });
        this.geolocateControl.on('trackuserlocationstart', () => {
            customButton.classList.add('is--active');
        });
        this.geolocateControl.on('trackuserlocationend', () => {
            customButton.classList.remove('is--active');
        });
        this.geolocateControl.on('error', () => {
            customButton.classList.remove('is--active');
        });
    }
    /**
     * Register callback for map load event
     * @param {Function} callback - Function to call when map loads
     */
    onLoad(callback) {
        if (typeof callback !== 'function') {
            throw new Error('Callback must be a function');
        }
        if (this.isStyleLoaded) {
            callback(this.map); // If already loaded, call immediately
        } else {
            this.onLoadCallbacks.push(callback); // Otherwise, queue it
        }
    }
    /**
     * Register callback for map error event
     * @param {Function} callback - Function to call on error
     */
    onError(callback) {
        if (typeof callback !== 'function') {
            throw new Error('Callback must be a function');
        }
        this.onErrorCallbacks.push(callback);
    }
    /**
     * Trigger all registered load callbacks
     * @private
     */
    _triggerLoadCallbacks() {
        this.onLoadCallbacks.forEach(callback => {
            try {
                callback(this.map);
            } catch (error) { // Silent error handling
            }
        });
    }
    /**
     * Play intro fly-to animation
     * Smoothly zooms in from initial view to final view
     * @private
     */
    _playIntroAnimation() {
        const targetZoom = this.isMobile 
            ? MapManager.CONFIG.INTRO_ZOOM_MOBILE
            : MapManager.CONFIG.INTRO_ZOOM_DESKTOP;
        const targetPitch = this.isMobile
            ? MapManager.CONFIG.MOBILE_PITCH
            : MapManager.CONFIG.DEFAULT_PITCH;
        this.map.flyTo({
            center: MapManager.CONFIG.DEFAULT_CENTER,
            zoom: targetZoom,
            pitch: targetPitch,
            bearing: MapManager.CONFIG.DEFAULT_BEARING,
            duration: MapManager.CONFIG.INTRO_DURATION,
            speed: MapManager.CONFIG.INTRO_SPEED,
            curve: MapManager.CONFIG.INTRO_CURVE,
            essential: true,
            easing: (t) => 1 - Math.pow(1 - t, 6) // easeOutSextic
        });
    }
    /**
     * Trigger all registered error callbacks
     * @private
     */
    _triggerErrorCallbacks(error) {
        this.onErrorCallbacks.forEach(callback => {
            try {
                callback(error);
            } catch (err) {
            }
        });
    }
    /**
     * Setup satellite toggle button
     * @private
     */
    _setupSatelliteToggle() {
        const toggleBtn = document.getElementById('toggle-satellite');
        if (!toggleBtn) return;
        toggleBtn.addEventListener('click', () => {
            this.toggleSatelliteView();
        });
    }
    /**
     * Toggle between standard and satellite view
     * Preserves all data layers (clusters, heatmap, markers)
     * @public
     */
    toggleSatelliteView() {
        if (!this.map) return;
        const toggleBtn = document.getElementById('toggle-satellite');
        this._saveDataLayers(); // ! Save current data layers before style switch
        this.isSatelliteView = !this.isSatelliteView; // Toggle state
        const newStyle = this.isSatelliteView // Switch style
            ? MapManager.CONFIG.SATELLITE_STYLE_URL 
            : this.config.STYLE_URL;
        this.map.setStyle(newStyle);
        if (toggleBtn) { // Update button state
            if (this.isSatelliteView) {
                toggleBtn.classList.add('is--active');
            } else {
                toggleBtn.classList.remove('is--active');
            }
        }
        this._toggleNavigationBlendMode(); // Toggle navigation blend mode
        this.map.once('style.load', () => { // Restore data layers after style loads
            this._restoreDataLayers();
        });
    }
    /**
     * ! Save references to data layers before style switch (For Satellite toggle)
     * @private
     */
    _saveDataLayers() {
        if (!this.map) return;
        this.dataLayers = [];
        const style = this.map.getStyle(); // Get all sources and layers that contain our data
        if (style && style.sources) {
            Object.keys(style.sources).forEach(sourceId => {
                if (sourceId.includes('locations-')) { // Save our custom data sources (clusters, heatmap)
                    const source = this.map.getSource(sourceId);
                    if (source && source._data) {
                        this.dataLayers.push({
                            sourceId: sourceId,
                            type: 'geojson',
                            data: source._data,
                            options: style.sources[sourceId]
                        });
                    }
                }
            });
        }
        if (style && style.layers) { // Store layer definitions
            style.layers.forEach(layer => {
                if (layer.source && layer.source.includes('locations-')) {
                    const existingLayer = this.dataLayers.find(d => d.sourceId === layer.source);
                    if (existingLayer) {
                        if (!existingLayer.layers) existingLayer.layers = [];
                        existingLayer.layers.push(layer); // Add layer to existing source entry
                    }
                }
            });
        }
    }
    /**
     * ! Restore data layers after style switch (For Satellite toggle)
     * @private
     */
    _restoreDataLayers() {
        if (!this.map || this.dataLayers.length === 0) return;
        this.dataLayers.forEach(layerData => { // Re-add sources and layers
            if (!this.map.getSource(layerData.sourceId)) { // Add source if it doesn't exist
                const sourceConfig = {
                    type: layerData.type,
                    data: layerData.data
                };
                if (layerData.options.cluster !== undefined) { // Add cluster config if it exists
                    sourceConfig.cluster = layerData.options.cluster;
                    sourceConfig.clusterMaxZoom = layerData.options.clusterMaxZoom;
                    sourceConfig.clusterRadius = layerData.options.clusterRadius;
                    if (layerData.options.clusterMinPoints) {
                        sourceConfig.clusterMinPoints = layerData.options.clusterMinPoints;
                    }
                }
                this.map.addSource(layerData.sourceId, sourceConfig);
            }
            if (layerData.layers) { // Re-add layers
                layerData.layers.forEach(layer => {
                    if (!this.map.getLayer(layer.id)) {
                        this.map.addLayer(layer);
                    }
                });
            }
        });
        // Notify that layers are restored (markers need to be re-rendered)
        if (window.markerManager && window.markerManager.markers.length > 0) {
            const currentZoom = this.map.getZoom(); // Ensure marker visibility is correct when restoring data on map style change
            if (window.markerManager.toggleMarkersVisibility) {
                window.markerManager.toggleMarkersVisibility(currentZoom);
            }
        }
    }
    /**
     * ! Toggle navigation blend mode based on satellite view state
     * @private
     */
    _toggleNavigationBlendMode() {
        const navigationEl = document.querySelector('.navigation');
        if (!navigationEl) return;
        if (this.isSatelliteView) {
            navigationEl.style.mixBlendMode = 'normal'; // Satellite mode ON - turn off blend mode difference
        } else {
            navigationEl.style.mixBlendMode = 'difference'; // Standard mode - restore blend mode difference
        }
    }
    /**
     * Fly to specific coordinates
     * @param {Object} options - Fly-to options
     * @param {Array|Object} options.center - [lng, lat] or {lng, lat}
     * @param {number} options.zoom - Target zoom level
     * @param {number} options.bearing - Target bearing (rotation)
     * @param {number} options.pitch - Target pitch (tilt)
     * @param {number} options.duration - Animation duration in ms
     */
    flyTo(options = {}) {
        if (!this.map) return;
        const flyOptions = {
            center: options.center,
            zoom: options.zoom !== undefined ? options.zoom : this.map.getZoom(),
            bearing: options.bearing !== undefined ? options.bearing : this.map.getBearing(),
            pitch: options.pitch !== undefined ? options.pitch : this.map.getPitch(),
            duration: options.duration !== undefined ? options.duration : 2000,
            essential: true
        };
        this.map.flyTo(flyOptions);
    }
    /**
     * Jump to specific coordinates (no animation)
     * @param {Object} options - Jump-to options
     */
    jumpTo(options = {}) {
        if (!this.map) return;
        this.map.jumpTo({
            center: options.center,
            zoom: options.zoom,
            bearing: options.bearing,
            pitch: options.pitch
        });
    }
    /**
     * Zoom in by one level
     * @param {number} duration - Animation duration in ms (default: 300)
     */
    zoomIn(duration = 300) {
        if (!this.map) return;
        const currentZoom = this.map.getZoom();
        this.map.easeTo({
            zoom: currentZoom + 1,
            duration: duration
        });
    }
    /**
     * Zoom out by one level
     * @param {number} duration - Animation duration in ms (default: 300)
     */
    zoomOut(duration = 300) {
        if (!this.map) return;
        const currentZoom = this.map.getZoom();
        this.map.easeTo({
            zoom: currentZoom - 1,
            duration: duration
        });
    }
    /**
     * Reset map orientation (bearing and pitch) to default
     * @param {number} duration - Animation duration in ms (default: 500)
     */
    resetOrientation(duration = 500) {
        if (!this.map) return;
        this.map.easeTo({
            bearing: this.config.DEFAULT_BEARING,
            pitch: this.config.DEFAULT_PITCH,
            duration: duration
        });
    }
    /**
     * Smart fly out - zooms out intelligently or returns to default view
     * If zoom > 8: zoom out to 65% of current zoom
     * If zoom ≤ 8: fly back to initial camera position
     */
    flyOut() {
        if (!this.map) return;
        const currentZoom = this.map.getZoom();
        if (currentZoom > 8) { // Zoom out to 65% of current zoom
            this.map.flyTo({
                zoom: currentZoom * 0.65,
                speed: 0.125,
                curve: 2,
                duration: 2500,
                essential: true,
                pitch: 0,
                bearing: 0,
                padding: { top: 0, bottom: 0, left: 0, right: 0 },
                easing: (t) => 1 - Math.pow(1 - t, 6)
            });
        } else { // Return to initial camera position
            this.map.flyTo({
                center: this.config.DEFAULT_CENTER,
                zoom: this.config.DEFAULT_ZOOM,
                curve: 2,
                speed: 0.125,
                duration: 3000,
                essential: true,
                pitch: this.config.DEFAULT_PITCH,
                bearing: this.config.DEFAULT_BEARING,
                padding: { top: 0, bottom: 0, left: 0, right: 0 },
                easing: (t) => 1 - Math.pow(1 - t, 6)
            });
        }
    }
    /**
     * Reset camera to default position
     */
    resetCamera() {
        this.flyTo({
            center: this.config.DEFAULT_CENTER,
            zoom: this.config.DEFAULT_ZOOM,
            bearing: this.config.DEFAULT_BEARING,
            pitch: this.config.DEFAULT_PITCH
        });
    }
    /**
     * Get current camera position
     * @returns {Object} Current camera state
     */
    getCameraState() {
        if (!this.map) {
            return null;
        }
        return {
            center: this.map.getCenter(),
            zoom: this.map.getZoom(),
            bearing: this.map.getBearing(),
            pitch: this.map.getPitch()
        };
    }
    /**
     * Set map style
     * @param {string} styleUrl - Mapbox style URL
     */
    setStyle(styleUrl) {
        if (!this.map) return;
        this.map.setStyle(styleUrl);
    }
    /**
     * Resize map (useful after container size change)
     */
    resize() {
        if (!this.map) return;
        this.map.resize();
    }
    /**
     * Get the underlying Mapbox GL map instance
     * @returns {mapboxgl.Map|null} Map instance
     */
    getMap() {
        return this.map;
    }
    /**
     * Check if map is ready for use
     * @returns {boolean} True if map is initialized and style is loaded
     */
    isReady() {
        return this.isInitialized && this.isStyleLoaded;
    }
    /**
     * Destroy map instance and clean up
     */
    destroy() {
        if (this.map) {
            this.map.remove();
            this.map = null;
            this.isInitialized = false;
            this.isStyleLoaded = false;
        }
    }
}
// ! EXPORT
export default MapManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') {
    window.MapManager = MapManager;
}